/* DreamCloud → Upload call report: the Google Ads "Call details" CSV, which the
 * worker matches to sales as call conversions.
 *
 *   choose or drop a .csv → Preview (call_report, preview: true: parsed, new,
 *   duplicate, skipped, nothing stored) → Store N new calls (preview: false)
 *   → what was stored, and how many exact call times were recovered.
 *
 * The flow is callReportStep in js/rules/dreamcloud.js: a file over 2 MB is
 * refused before it is read, and a file the worker cannot parse stays at the
 * first step with the worker's sentence, so the store button never appears for
 * it. Only counts are shown; the worker's redacted samples are not.
 */
import * as Vue from 'vue';
import { dreamcloud } from '../dreamcloud.js';
import { initialCallReport, callReportStep, errorText, MAX_CSV_BYTES } from '../rules/dreamcloud.js';

var SKIPPED = { noPhone: 'no phone number', noTime: 'no call time', total: 'totals row' };

function said(err) { return errorText(err); }

export const DcCallReport = {
  name: 'DcCallReport',
  emits: ['close', 'busy'],
  setup: function (props, ctx) {
    var s = Vue.ref(initialCallReport());
    var busy = Vue.ref(false);
    var over = Vue.ref(false);
    function go(e) { s.value = callReportStep(s.value, e); }

    async function take(file) {
      if (!file || busy.value) return;
      // Too large is decided on the size alone, before reading a byte.
      if (file.size > MAX_CSV_BYTES) { go({ type: 'file', name: file.name, size: file.size }); return; }
      var text = '';
      try { text = await file.text(); } catch (e) { text = ''; }
      go({ type: 'file', name: file.name, size: file.size, csv: text });
    }

    function onPick(ev) {
      var f = ev.target.files && ev.target.files[0];
      take(f);
      ev.target.value = '';
    }
    function onDrop(ev) {
      over.value = false;
      var f = ev.dataTransfer && ev.dataTransfer.files && ev.dataTransfer.files[0];
      take(f);
    }

    async function preview() {
      if (busy.value || s.value.step !== 'pick' || !s.value.csv) return;
      busy.value = true;
      try {
        go({ type: 'preview', response: await dreamcloud.invoke('call_report', { csv: s.value.csv, preview: true }) });
      } catch (err) {
        go({ type: 'preview', response: { error: said(err) } });
      } finally {
        busy.value = false;
      }
    }

    async function store() {
      if (busy.value) return;
      go({ type: 'store' });
      if (s.value.step !== 'storing') return;
      busy.value = true;
      ctx.emit('busy', true);
      dreamcloud.beginWrite();
      try {
        go({ type: 'stored', response: await dreamcloud.invoke('call_report', { csv: s.value.csv, preview: false }) });
      } catch (err) {
        go({ type: 'failed', error: said(err) });
      } finally {
        busy.value = false;
        var r = s.value;
        dreamcloud.endWrite(r.step === 'done'
          ? { ok: true, text: 'Call report: stored ' + (r.result.submitted || 0) + ' new calls from ' + (r.name || 'the file') + '.' }
          : { ok: false, text: 'Call report: ' + (r.error || 'the store failed') + '. How many were stored is not known; preview the file again and the stored calls show as already stored.' });
        ctx.emit('busy', false);
      }
      dreamcloud.load();
    }

    function skippedLines(sk) {
      if (!sk) return [];
      return Object.keys(sk).filter(function (k) { return Number(sk[k]) > 0; }).map(function (k) {
        return sk[k] + ' ' + (SKIPPED[k] || k);
      });
    }

    var newCalls = Vue.computed(function () { return Number((s.value.preview && s.value.preview.new) || 0); });

    function close() { if (!busy.value || s.value.step !== 'storing') ctx.emit('close'); }

    return {
      s: s, busy: busy, over: over, onPick: onPick, onDrop: onDrop, preview: preview, store: store,
      close: close, skippedLines: skippedLines, newCalls: newCalls,
      reset: function () { go({ type: 'reset' }); }
    };
  },
  template: [
    '<div class="modal-scrim" @click.self="close">',
    '  <div class="sheet dc-sheet dc-sheet--narrow" role="dialog" aria-modal="true" aria-labelledby="dc-calls-h">',
    '    <div class="dc-sheet__head">',
    '      <h3 id="dc-calls-h">Upload call report</h3>',
    '      <button type="button" class="dc-x" aria-label="Close" @click="close">&times;</button>',
    '    </div>',
    '    <div class="dc-sheet__body">',
    '      <p class="dc-muted">The Google Ads &ldquo;Call details&rdquo; export, as a CSV (at most 2 MB).</p>',
    '',
    '      <label class="dc-drop" :class="{ \'is-over\': over }"',
    '        @dragover.prevent="over = true" @dragleave="over = false" @drop.prevent="onDrop">',
    '        <input type="file" accept=".csv,text/csv" class="dc-sr" @change="onPick" :disabled="busy || s.step === \'storing\'" />',
    '        <span v-if="s.name"><strong>{{ s.name }}</strong></span>',
    '        <span v-else>Drop the CSV here, or <u>choose a file</u></span>',
    '      </label>',
    '',
    '      <p v-if="s.error && s.step !== \'failed\'" class="dc-error" role="alert">{{ s.error }}</p>',
    '',
    '      <dl v-if="s.step === \'previewed\'" class="dc-counts" role="status">',
    '        <div><dt>Calls in the file</dt><dd>{{ s.preview.parsed }}</dd></div>',
    '        <div><dt>New</dt><dd>{{ s.preview.new || 0 }}</dd></div>',
    '        <div><dt>Already stored</dt><dd>{{ s.preview.duplicate || 0 }}</dd></div>',
    '        <div class="dc-counts__wide" v-if="skippedLines(s.preview.skipped).length"><dt>Skipped</dt>',
    '          <dd>{{ skippedLines(s.preview.skipped).join(\' · \') }}</dd></div>',
    '      </dl>',
    '      <p v-if="s.step === \'previewed\'" class="dc-muted">Nothing has been stored yet.</p>',
    '',
    '      <template v-if="s.step === \'done\'">',
    '        <p class="dc-ok" role="status">Stored.</p>',
    '        <dl class="dc-counts">',
    '          <div><dt>Calls in the file</dt><dd>{{ s.result.parsed }}</dd></div>',
    '          <div><dt>Stored</dt><dd>{{ s.result.submitted || 0 }}</dd></div>',
    '          <div><dt>Already stored</dt><dd>{{ s.result.deduped || 0 }}</dd></div>',
    '          <div class="dc-counts__wide" v-if="skippedLines(s.result.skipped).length"><dt>Skipped</dt>',
    '            <dd>{{ skippedLines(s.result.skipped).join(\' · \') }}</dd></div>',
    '          <div class="dc-counts__wide"><dt>Exact call times</dt>',
    '            <dd v-if="s.result.exactTimes">{{ s.result.exactTimes.resolved || 0 }} recovered of {{ s.result.exactTimes.pending || 0 }}',
    '              <span v-if="s.result.exactTimes.ambiguous"> · {{ s.result.exactTimes.ambiguous }} ambiguous</span>',
    '              <span v-if="s.result.exactTimes.unmatched"> · {{ s.result.exactTimes.unmatched }} unmatched</span></dd>',
    '            <dd v-else>Not recovered now; the daily sweep tries again.</dd></div>',
    '        </dl>',
    '      </template>',
    '      <p v-if="s.step === \'failed\'" class="dc-error" role="alert">{{ s.error }}</p>',
    '      <p v-if="s.step === \'failed\'" class="dc-muted">How many calls were stored is not known. Preview the file',
    '        again: calls that were stored show as already stored.</p>',
    '    </div>',
    '',
    '    <div class="dc-sheet__foot">',
    '      <button v-if="s.step === \'pick\'" type="button" class="btn btn--primary"',
    '        :disabled="busy || !s.csv" @click="preview">{{ busy ? \'Reading…\' : \'Preview\' }}</button>',
    '      <button v-if="s.step === \'previewed\' || s.step === \'storing\'" type="button" class="btn btn--danger"',
    '        :disabled="busy || !newCalls" @click="store">',
    '        {{ s.step === \'storing\' ? \'Storing…\' : newCalls ? \'Store \' + newCalls + \' new call\' + (newCalls === 1 ? \'\' : \'s\') : \'No new calls to store\' }}</button>',
    '      <button v-if="s.step === \'done\' || s.step === \'failed\'" type="button" class="btn btn--quiet" @click="reset">Another file</button>',
    '      <button type="button" class="btn btn--quiet" @click="close">Close</button>',
    '    </div>',
    '  </div>',
    '</div>'
  ].join('\n')
};
